import { graphql, Link, PageProps } from "gatsby"
import React, { useEffect, useRef, useState } from "react"
import { ArrowRight, Mail, Phone } from "react-feather"
import ScrollIntoView from "react-scroll-into-view"
import { Form } from "../components/contact"
import ItemBlog from "../components/item-blog"
import ItemPortfolio from "../components/item-portfolio"
import Layout from "../components/layout"
import { Button } from "../components/ui"
import SocialLinks from "../utils/sociallinks"
import { IndexPageQuery } from "./__generated__/IndexPageQuery"

export default ({ data, location }: PageProps<IndexPageQuery>) => {
    const siteData = data.site.siteMetadata

    const ofertaItems = data.oferta.edges

    const portfolioList = ofertaItems.map((item, _) => (
        <ItemPortfolio
            data={item.node}
            key={`p-item-index-${item.node.id}`}
            index={_}
            showIndex={ofertaItems.length > 1}
            even={(_ + 1) % 2 === 0}
        />
    ))

    const blogList = data.blog.edges
        .slice(0, 3)
        .map(item => (
            <ItemBlog data={item.node} key={`b-item-index-${item.node.id}`} />
        ))

    return (
        <Layout
            front={true}
            seo={{
                title:
                    "Fizjoterapeuta koni Mazowieckie | Rehabilitacja konia | Derka magnetyczna",
                description: siteData.description,
            }}
            navPlaceholder={false}
            location={location}
        >
            <Wall data={siteData} />
            {siteData.about !== "" && (
                <About data={siteData.about} dataMore={siteData.aboutMore} />
            )}
            <Oferta>{portfolioList}</Oferta>
            <FaqCta />
            <Blog>{blogList}</Blog>
            <Contact data={siteData.contact} />
        </Layout>
    )
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                       */
/* -------------------------------------------------------------------------- */

const Wall = ({ data }) => {
    const wall = useRef(null)

    const [loaded, setLoaded] = useState(false)

    // The hero clip is portrait, so it only makes sense below the lg breakpoint.
    // Deciding client-side keeps desktop from downloading the video at all.
    const [useVideo, setUseVideo] = useState(false)

    useEffect(() => {
        setLoaded(true)

        if (!window.matchMedia) return

        const narrow = window.matchMedia("(max-width: 1023px)")
        const calm = window.matchMedia("(prefers-reduced-motion: reduce)")

        const update = () => setUseVideo(narrow.matches && !calm.matches)
        update()

        // addListener is deprecated but still needed for older mobile Safari.
        if (narrow.addEventListener) {
            narrow.addEventListener("change", update)
            calm.addEventListener("change", update)
            return () => {
                narrow.removeEventListener("change", update)
                calm.removeEventListener("change", update)
            }
        }

        narrow.addListener(update)
        calm.addListener(update)
        return () => {
            narrow.removeListener(update)
            calm.removeListener(update)
        }
    }, [])

    const titleParts = String(data.title)
        .split("|")
        .map(p => p.trim())
        .filter(Boolean)

    return (
        <section className="hero" ref={wall}>
            <div className="hero-media">
                {useVideo ? (
                    <video
                        className="hero-video"
                        poster={data.titleImage}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        aria-hidden="true"
                        tabIndex={-1}
                    >
                        <source src="/video/hero-mobile.mp4" type="video/mp4" />
                    </video>
                ) : (
                    <img src={data.titleImage} alt="" aria-hidden="true" />
                )}
            </div>
            <div className="hero-scrim"></div>

            <div className="hero-inner">
                <div className="large-container">
                    <div className="max-w-4xl">
                        <p
                            className="eyebrow eyebrow-line reveal"
                            style={{ color: "#DCC79A" }}
                        >
                            {data.introTag}
                        </p>

                        <h1
                            className={`display-1 mt-6 reveal reveal-d1 ${
                                data.capitalizeTitleOnHome ? "uppercase" : ""
                            }`}
                        >
                            {titleParts.map((part, i) => (
                                <span className="block" key={`t-${i}`}>
                                    {part}
                                    {i < titleParts.length - 1 && (
                                        <span
                                            className="ml-3"
                                            style={{
                                                color: "#C9A46A",
                                                opacity: 0.7,
                                            }}
                                        >
                                            |
                                        </span>
                                    )}
                                </span>
                            ))}
                        </h1>

                        <p
                            className="lede mt-8 max-w-2xl reveal reveal-d2"
                            style={{ color: "rgba(251,249,245,0.86)" }}
                        >
                            {data.description}
                        </p>

                        <div className="mt-12 flex flex-wrap items-center reveal reveal-d3">
                            <ScrollIntoView selector="#oferta">
                                <Button
                                    title="Oferta"
                                    type="button"
                                    variant="light"
                                    iconRight={<ArrowRight />}
                                />
                            </ScrollIntoView>
                            <ScrollIntoView selector="#kontakt">
                                <button
                                    className="link-quiet ml-8"
                                    style={{ color: "rgba(251,249,245,0.9)" }}
                                >
                                    Umów wizytę
                                </button>
                            </ScrollIntoView>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={`hidden lg:block absolute bottom-0 right-0 mr-16 transition-opacity duration-1000 ${
                    loaded ? "opacity-100" : "opacity-0"
                }`}
            >
                <span className="hero-scroll-cue block"></span>
            </div>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Section heading                                                            */
/* -------------------------------------------------------------------------- */

const SectionHeading = ({ eyebrow, title, children = null, center = false }) => (
    <div className={`reveal ${center ? "text-center" : ""}`}>
        <p
            className={`eyebrow ${
                center ? "" : "eyebrow-line"
            } ${center ? "block" : ""}`}
        >
            {eyebrow}
        </p>
        <h2 className="display-2 text-color-1 mt-5">{title}</h2>
        {children && (
            <div className={`mt-6 ${center ? "max-w-2xl mx-auto" : ""}`}>
                {children}
            </div>
        )}
    </div>
)

/* -------------------------------------------------------------------------- */
/* About                                                                      */
/* -------------------------------------------------------------------------- */

const About = ({ data, dataMore }) => {
    return (
        <section className="section" id="o-mnie">
            <div className="large-container">
                <div className="lg:flex lg:items-center">
                    <div className="lg:w-1/2 lg:pr-20">
                        <div className="frame reveal">
                            <img
                                src="/images/main2.jpeg"
                                alt="Fizjoterapia koni — praca z pacjentem"
                                className="w-full"
                                style={{
                                    height: "34rem",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                }}
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="lg:w-1/2 mt-12 lg:mt-0">
                        <SectionHeading
                            eyebrow="Poznajmy się"
                            title="O mnie"
                        />
                        <div className="reveal reveal-d1">
                            <p className="mt-8 text-color-default">{data}</p>
                            <p className="mt-6 text-color-2">{dataMore}</p>
                            <div className="rule-short mt-10"></div>
                            <div className="mt-10">
                                <Button
                                    to="/about"
                                    variant="ghost"
                                    title="Więcej o Horse Physio"
                                    iconRight={<ArrowRight />}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Oferta                                                                     */
/* -------------------------------------------------------------------------- */

const Oferta = ({ children }) => {
    return (
        <section className="bg-bgalt overflow-hidden" id="oferta">
            <div className="large-container pt-20 lg:pt-32">
                <p className="eyebrow eyebrow-line reveal">Usługi i cennik</p>
            </div>
            <div className="pb-12 lg:pb-24">{children}</div>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Blog                                                                       */
/* -------------------------------------------------------------------------- */

const Blog = ({ children }) => {
    return (
        <section className="section">
            <div className="large-container">
                <div className="lg:flex lg:items-end lg:justify-between">
                    <SectionHeading eyebrow="Artykuły" title="Blog" />
                    <div className="mt-8 lg:mt-0 reveal">
                        <Link to="/blog" className="link-quiet">
                            Wszystkie wpisy
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap -mx-4 mt-16 reveal">
                    {children}
                </div>
            </div>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* FAQ call to action                                                         */
/* -------------------------------------------------------------------------- */

const FaqCta = () => {
    return (
        <section className="bg-bgdeep">
            <div className="large-container py-24 lg:py-32 text-center">
                <p
                    className="eyebrow reveal"
                    style={{ color: "#C9A46A" }}
                >
                    Pytania i odpowiedzi
                </p>
                <h2
                    className="display-2 mt-5 reveal reveal-d1"
                    style={{ color: "#FBF9F5" }}
                >
                    Najczęstsze pytania
                </h2>
                <p
                    className="lede mt-6 max-w-2xl mx-auto reveal reveal-d2"
                    style={{ color: "rgba(251,249,245,0.72)" }}
                >
                    Znajdziesz tu odpowiedzi na pytania dotyczące pierwszej
                    wizyty, przygotowania konia oraz rehabilitacji po urazach i
                    zabiegach.
                </p>
                <div className="mt-12 reveal reveal-d3">
                    <Button
                        to="/faq"
                        variant="light"
                        label="Przejdź do najczęstszych pytań"
                        title="Zobacz FAQ"
                        iconRight={<ArrowRight />}
                    />
                </div>
            </div>
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Contact                                                                    */
/* -------------------------------------------------------------------------- */

const Contact = ({ data }) => {
    const hasContactForm = data.api_url

    return (
        <section className="bg-bgalt" id="kontakt">
            <div className="large-container py-20 lg:py-32">
                <div className="lg:flex lg:justify-between">
                    <div className="lg:w-1/2 lg:pr-16">
                        <SectionHeading
                            eyebrow="Zapraszam do kontaktu"
                            title="Kontakt"
                        >
                            <p className="lede">{data.description}</p>
                        </SectionHeading>

                        <ul className="mt-12 reveal reveal-d1">
                            {data.mail && (
                                <li className="flex items-center py-5 border-t border-medium">
                                    <span className="text-secondary">
                                        <Mail size={18} strokeWidth={1.4} />
                                    </span>
                                    <a
                                        className="ml-5 text-color-1 text-lg"
                                        href={"mailto:" + data.mail}
                                    >
                                        {data.mail}
                                    </a>
                                </li>
                            )}
                            {data.phone && (
                                <li className="flex items-center py-5 border-t border-b border-medium">
                                    <span className="text-secondary">
                                        <Phone size={18} strokeWidth={1.4} />
                                    </span>
                                    <a
                                        className="ml-5 text-color-1 text-lg"
                                        href={"tel:" + data.phone}
                                    >
                                        {data.phone}
                                    </a>
                                </li>
                            )}
                        </ul>

                        <div className="mt-10 reveal reveal-d2">
                            <p className="meta mb-4">Obserwuj</p>
                            <SocialLinks />
                        </div>
                    </div>

                    <div className="lg:w-1/2 mt-16 lg:mt-0">
                        {hasContactForm ? (
                            <Form api={data.api_url} />
                        ) : (
                            <div className="frame reveal reveal-d1">
                                <img
                                    src="/images/main1.jpeg"
                                    alt="Fizjoterapia koni"
                                    className="w-full"
                                    style={{
                                        height: "32rem",
                                        objectFit: "cover",
                                    }}
                                    loading="lazy"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export const query = graphql`
    query IndexPageQuery {
        site: site {
            siteMetadata {
                title
                description
                capitalizeTitleOnHome
                titleImage
                ogImage
                twoColumnWall
                introTag
                description
                about
                aboutMore
                contact {
                    api_url
                    description
                    mail
                    phone
                    address
                }
                social {
                    name
                    url
                    icon
                }
            }
        }
        oferta: allMdx(
            filter: { fields: { sourceName: { eq: "oferta" } } }
            limit: 6
        ) {
            edges {
                node {
                    id
                    frontmatter {
                        title
                        description
                        image {
                            childImageSharp {
                                fluid(maxWidth: 1000) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    fields {
                        slug
                    }
                }
            }
        }
        blog: allMdx(
            filter: { fields: { sourceName: { eq: "blog" } } }
            sort: { fields: [frontmatter___date], order: DESC }
            limit: 6
        ) {
            edges {
                node {
                    id
                    frontmatter {
                        title
                        description
                        date(formatString: "DD MMMM YYYY")
                        image {
                            childImageSharp {
                                fluid(maxWidth: 1000) {
                                    ...GatsbyImageSharpFluid
                                }
                            }
                        }
                    }
                    fields {
                        slug
                    }
                }
            }
        }
    }
`
